/**
 * ChangelogConfirmPage — public landing for the double-opt-in
 * confirm link emailed by /api/changelog/subscribe.
 *
 * Hits GET /api/changelog/confirm/{token}, then renders one of three
 * states:
 *   • busy   — calling backend
 *   • ok     — confirmed (or already confirmed)
 *   • bad    — token unknown or backend failure
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, Warning, Sparkle, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function ChangelogConfirmPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ phase: "busy", payload: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/changelog/confirm/${encodeURIComponent(token || "")}`);
        if (!alive) return;
        const ok = r.data?.ok && r.data?.found;
        setState({ phase: ok ? "ok" : "bad", payload: r.data || null });
      } catch (e) {
        if (!alive) return;
        setState({ phase: "bad", payload: { error: e?.response?.data?.detail || "Network error" } });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const isOk = state.phase === "ok";
  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900 flex items-center justify-center p-4"
         data-testid="changelog-confirm-page">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md shadow-sm p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkle size={18} weight="fill" className="text-blue-600" />
          <h1 className="text-base font-bold tracking-tight">What's New</h1>
        </div>
        {state.phase === "busy" && (
          <p className="text-sm text-slate-500" data-testid="confirm-busy">Confirming your subscription…</p>
        )}
        {isOk && (
          <div data-testid="confirm-success" className="space-y-2">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle size={18} weight="fill" />
              <p className="text-sm font-bold">
                {state.payload?.already
                  ? "You're already subscribed — nothing to do."
                  : "Subscription confirmed."}
              </p>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              We'll send a short digest to <span className="font-mono">{state.payload?.email}</span> in your selected language ({(state.payload?.lang || "en").toUpperCase()}) only when there's something new. No daily noise — promise.
            </p>
            <button
              data-testid="confirm-go-whatsnew"
              onClick={() => nav("/whats-new")}
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-blue-700 text-white hover:bg-blue-800">
              See what's new <ArrowRight size={11} weight="bold" />
            </button>
          </div>
        )}
        {state.phase === "bad" && (
          <div data-testid="confirm-bad" className="space-y-2">
            <div className="flex items-center gap-2 text-rose-700">
              <Warning size={18} weight="fill" />
              <p className="text-sm font-bold">We couldn't confirm this link.</p>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              The link may have expired, already been used, or copied incorrectly. Try subscribing again from the What's New page.
            </p>
            <button
              data-testid="confirm-retry"
              onClick={() => nav("/whats-new")}
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-slate-900 text-white hover:bg-slate-800">
              Back to What's New
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
